<script setup lang="ts">
import { ref } from 'vue';
import { UNASSIGNED } from '@/constants';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import { useWorksheetComposable } from './useWorksheetComposable';
import DateWorksheetEditor from '@/components/modal/worksheet/DateWorksheetEditor.vue';
import LinkedQuotesEditor from '@/components/modal/worksheet/LinkedQuotesEditor.vue';

interface Props {
  tourQuote: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  changeDate: [value: any];
  changeLinkedQuote: [value: any];
}>();

const { authorizations } = useWorksheetComposable();
const canEditExpiry = authorizations?.worksheet_tour_quote_header?.expiry_date_edit;
const canEditDateBooked = authorizations?.worksheet_tour_quote_header?.date_booked_edit;

const lang = getCurrentLocale();

const dates = ref<any>({
  expires: props.tourQuote.expires,
  bookedDate: props.tourQuote.bookedDate
});

const changeDate = (event: any, type: string) => {
  dates.value[type] = event;
  emits('changeDate', { value: event, type: type });
};

const changeLinkedQuote = (event: any) => {
  emits('changeLinkedQuote', { value: event });
};
</script>
<template>
  <div class="tour-info-cpn">
    <section id="header" class="my-3 px-3">
      <div class="grid">
        <div class="col-6 text-lg font-bold">{{ $t('worksheet.groups_quote') }}</div>
        <div class="col-6 text-right text-lg font-bold">({{ tourQuote?.sk.includes('TR') ? $t('worksheet.tour_instaquote') : $t('worksheet.tour_manual_quote') }})</div>
      </div>
    </section>
    <section id="information" class="px-3">
      <div class="grid">
        <div class="col-3">
          <div class="quote-id font-bold">
            <div class="grid">
              <div class="col-6 mb-4">{{ $t('worksheet.quote_id') }}:</div>
              <div class="col-6 font-normal">{{ tourQuote?.sk }}</div>
            </div>
          </div>
          <LinkedQuotesEditor :worksheetData="tourQuote" :permission="canEditExpiry" @changeLinkedQuote="changeLinkedQuote($event)" />
        </div>
        <div class="col-4">
          <div class="date-quote font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.date_quoted') }}:</div>
              <div class="col-8 font-normal">{{ formatDate(tourQuote?.created, 'dd-LLL-yyyy', true, lang) }}</div>
            </div>
          </div>
          <div class="expiry-date">
            <DateWorksheetEditor :title="$t('worksheet.expiry_date')" :date="dates?.expires" :permission="canEditExpiry" @changeDate="changeDate($event, 'expires')" />
          </div>
          <div class="book-date">
            <DateWorksheetEditor :title="$t('worksheet.book_dated')" :date="dates?.bookedDate" :permission="canEditDateBooked" @changeDate="changeDate($event, 'bookedDate')" />
          </div>
        </div>
        <div class="col-5">
          <div class="quote-assigned-to font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.quote_assigned_to') }}:</div>
              <div class="col-8 font-normal">{{ tourQuote?.assignedTo === UNASSIGNED ? $t('status.unassigned') : tourQuote?.assignedTo }}</div>
            </div>
          </div>
          <div class="book-by font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_by') }}:</div>
              <div class="col-8 font-normal flex gap-1">
                <div>{{ tourQuote?.user }}</div>
                <div>{{ tourQuote?.email && `(${tourQuote?.email})` }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
